import ExpressionSystem, { ExpressionParameter } from 'common/components/expressionButton/ExpressionSystem';
import { BudgetConfigDto } from 'api/budgets/models/BudgetDto';

/**
 * Lightweight, config-only evaluation of budget summary configs.
 *
 * It resolves cross-references between configs (iteratively) using each config's plain value or
 * expression, WITHOUT the heavy budget totals/element-type parameters. This is enough for
 * "execution_study_hours" (= ROUNDDOWN(execution_study_cost / technician_cost; 0)), whose inputs
 * are plain values. The full evaluation lives in BudgetSummary.tsx (evaluatedConfigs).
 */
export function evaluateBudgetConfigs(configs: BudgetConfigDto[]): Map<string, number> {
    const list = configs ?? [];
    const map = new Map<string, { expression: string; value: number }>();
    list.forEach(c => map.set(c.key, { expression: c.expression ?? '', value: c.value ?? 0 }));

    const maxIterations = 10;
    for (let iter = 0; iter < maxIterations; iter++) {
        let changed = false;
        list.forEach(c => {
            const entry = map.get(c.key)!;
            if (!entry.expression) {
                return; // keep plain value
            }

            const params: ExpressionParameter[] = list
                .filter(o => o.key !== c.key)
                .map(o => ({
                    id: ExpressionSystem.formatParameterId([o.key]),
                    name: o.key,
                    value: map.get(o.key)!.value,
                }));

            const result = ExpressionSystem.evaluate(entry.expression, params);
            const newValue = result.error ? 0 : result.value;
            if (newValue !== entry.value) {
                entry.value = newValue;
                changed = true;
            }
        });
        if (!changed) {
            break;
        }
    }

    const out = new Map<string, number>();
    map.forEach((v, k) => out.set(k, v.value));
    return out;
}

export function evaluateExecutionStudyHours(configs: BudgetConfigDto[]): number {
    return evaluateBudgetConfigs(configs).get('execution_study_hours') ?? 0;
}
